import { useEffect, useState, useCallback } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  KeyIcon, PlusIcon, CopyIcon, ArrowsClockwiseIcon, ProhibitIcon,
  ClockCounterClockwiseIcon, WarningCircleIcon,
} from "@phosphor-icons/react";

const PERMS = [
  { id: "read_stations", label: "Read stations", hint: "GET /api/partner/stations" },
  { id: "post_stations", label: "Push stations", hint: "POST /api/partner/stations" },
  { id: "post_audits", label: "Post audit data", hint: "POST /api/partner/audits" },
  { id: "post_reports", label: "Post driver reports", hint: "POST /api/partner/reports" },
];
const PERM_LABEL = Object.fromEntries(PERMS.map((p) => [p.id, p.label]));

function fmt(ts) {
  if (!ts) return "—";
  try { return new Date(ts).toLocaleString(); } catch { return ts; }
}

function StatusBadge({ status }) {
  const active = status === "active";
  return (
    <span data-testid={`partner-status-${status}`}
      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${
        active ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
               : "bg-rose-50 text-rose-700 border border-rose-200"}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${active ? "bg-emerald-500" : "bg-rose-500"}`} />
      {active ? "Active" : "Revoked"}
    </span>
  );
}

function KeyRevealDialog({ open, data, onClose }) {
  const copy = () => { navigator.clipboard.writeText(data?.api_key || ""); toast.success("API key copied"); };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="api-key-reveal-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><KeyIcon size={20} weight="duotone" /> API key generated</DialogTitle>
          <DialogDescription>
            Copy this key now for <span className="font-medium">{data?.partner?.name}</span>. For security it is stored hashed and
            <span className="font-semibold text-rose-600"> cannot be shown again.</span>
          </DialogDescription>
        </DialogHeader>
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 flex items-start gap-2 text-xs text-amber-800">
          <WarningCircleIcon size={18} className="shrink-0 mt-0.5" />
          Store it in your partner's server-side config. Never expose it in a browser or mobile bundle.
        </div>
        <div className="flex items-center gap-2">
          <code data-testid="revealed-api-key" className="flex-1 break-all rounded-lg bg-slate-900 px-3 py-2.5 font-mono text-xs text-emerald-300">
            {data?.api_key}
          </code>
          <Button size="icon" variant="secondary" onClick={copy} data-testid="copy-api-key-btn"><CopyIcon size={16} /></Button>
        </div>
        <DialogFooter>
          <Button onClick={onClose} data-testid="close-key-dialog-btn">Done</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CreatePartnerDialog({ open, onClose, onCreated }) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [perms, setPerms] = useState([]);
  const [pem, setPem] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (open) { setName(""); setDescription(""); setPerms([]); setPem(""); } }, [open]);

  const toggle = (id) => setPerms((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

  const submit = async () => {
    if (!name.trim()) { toast.error("Partner name is required"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/partners/register", {
        name, description: description || null, permissions: perms,
        rsa_public_key_pem: pem.trim() || null,
      });
      toast.success("Partner created");
      onCreated(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="create-partner-dialog" className="max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>New partner</DialogTitle>
          <DialogDescription>Register an external app and issue it a scoped API key.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="p-name">Partner name</Label>
            <Input id="p-name" data-testid="partner-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Acme Charging App" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="p-desc">Description</Label>
            <Input id="p-desc" data-testid="partner-description-input" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Optional" />
          </div>
          <div className="space-y-2">
            <Label>Permissions</Label>
            {PERMS.map((p) => (
              <label key={p.id} className="flex items-start gap-3 rounded-lg border border-slate-200 p-3 cursor-pointer hover:bg-slate-50">
                <Checkbox checked={perms.includes(p.id)} onCheckedChange={() => toggle(p.id)} data-testid={`perm-${p.id}`} className="mt-0.5" />
                <div>
                  <div className="text-sm font-medium text-slate-800">{p.label}</div>
                  <div className="text-xs text-slate-400 font-mono">{p.hint}</div>
                </div>
              </label>
            ))}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="p-pem">RSA public key (optional)</Label>
            <Textarea id="p-pem" data-testid="partner-pem-input" value={pem} onChange={(e) => setPem(e.target.value)} rows={4}
              className="font-mono text-xs" placeholder={"-----BEGIN PUBLIC KEY-----\n...\n-----END PUBLIC KEY-----"} />
            <p className="text-xs text-slate-400">Used to verify end-user tokens (X-User-Token, RS256 JWT).</p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} data-testid="cancel-create-partner-btn">Cancel</Button>
          <Button onClick={submit} disabled={busy} data-testid="submit-create-partner-btn">{busy ? "Creating…" : "Create partner"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Partners() {
  const [partners, setPartners] = useState([]);
  const [logs, setLogs] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [keyData, setKeyData] = useState(null);
  const [filterPartner, setFilterPartner] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const loadPartners = useCallback(() => api.get("/partners").then((r) => setPartners(r.data)), []);
  const loadLogs = useCallback(() => {
    const params = {};
    if (filterPartner) params.partner_id = filterPartner;
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    return api.get("/partners/logs", { params }).then((r) => setLogs(r.data));
  }, [filterPartner, dateFrom, dateTo]);

  useEffect(() => { loadPartners(); }, [loadPartners]);
  useEffect(() => { loadLogs(); }, [loadLogs]);

  const revoke = async (p) => {
    try { await api.post(`/partners/${p.id}/revoke`); toast.success(`${p.name} revoked`); loadPartners(); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };
  const regenerate = async (p) => {
    try { const { data } = await api.post(`/partners/${p.id}/regenerate`); setKeyData(data); loadPartners(); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };

  return (
    <div className="p-5 lg:p-8 space-y-6" data-testid="partners-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl lg:text-3xl font-semibold text-slate-900">Partners</h1>
          <p className="text-sm text-slate-500 mt-1">Manage API keys for external apps consuming ChargeSure data.
            {" "}<a href={`${process.env.REACT_APP_BACKEND_URL}/api/docs`} target="_blank" rel="noreferrer" className="text-emerald-600 hover:underline">API docs →</a></p>
        </div>
        <Button onClick={() => setShowCreate(true)} data-testid="new-partner-btn"><PlusIcon size={16} className="mr-1" /> New partner</Button>
      </div>

      {/* Partners table */}
      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="partners-table">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-400">
              <tr>
                <th className="px-4 py-3 font-medium">Partner</th>
                <th className="px-4 py-3 font-medium">Permissions</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 font-medium">Key</th>
                <th className="px-4 py-3 font-medium">Created</th>
                <th className="px-4 py-3 font-medium">Last used</th>
                <th className="px-4 py-3 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {partners.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-400">No partners yet. Create one to issue an API key.</td></tr>
              )}
              {partners.map((p) => (
                <tr key={p.id} data-testid={`partner-row-${p.id}`} className="hover:bg-slate-50/60">
                  <td className="px-4 py-3">
                    <div className="font-medium text-slate-900">{p.name}</div>
                    {p.description && <div className="text-xs text-slate-400">{p.description}</div>}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1">
                      {(p.permissions || []).length === 0 && <span className="text-xs text-slate-300">none</span>}
                      {(p.permissions || []).map((perm) => (
                        <span key={perm} className="rounded-full bg-slate-100 border border-slate-200 px-2 py-0.5 text-xs text-slate-600">{PERM_LABEL[perm] || perm}</span>
                      ))}
                    </div>
                  </td>
                  <td className="px-4 py-3"><StatusBadge status={p.status} /></td>
                  <td className="px-4 py-3 font-mono text-xs text-slate-500">…{p.key_last4}</td>
                  <td className="px-4 py-3 text-xs text-slate-500">{fmt(p.created_at)}</td>
                  <td className="px-4 py-3 text-xs text-slate-500">{fmt(p.last_used_at)}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-2">
                      <Button size="sm" variant="outline" onClick={() => regenerate(p)} data-testid={`regenerate-${p.id}`}>
                        <ArrowsClockwiseIcon size={14} className="mr-1" /> Regenerate
                      </Button>
                      {p.status === "active" && (
                        <Button size="sm" variant="ghost" className="text-rose-600 hover:text-rose-700 hover:bg-rose-50" onClick={() => revoke(p)} data-testid={`revoke-${p.id}`}>
                          <ProhibitIcon size={14} className="mr-1" /> Revoke
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Request logs */}
      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
        <div className="flex flex-wrap items-end gap-3 border-b border-slate-100 p-4">
          <div className="flex items-center gap-2 text-slate-700 font-medium mr-2"><ClockCounterClockwiseIcon size={18} weight="duotone" /> API request logs</div>
          <div className="space-y-1">
            <Label className="text-xs text-slate-400">Partner</Label>
            <select data-testid="log-partner-filter" value={filterPartner} onChange={(e) => setFilterPartner(e.target.value)}
              className="block rounded-lg border border-slate-200 px-3 py-1.5 text-sm">
              <option value="">All partners</option>
              {partners.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs text-slate-400">From</Label>
            <Input type="date" data-testid="log-date-from" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="w-40" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs text-slate-400">To</Label>
            <Input type="date" data-testid="log-date-to" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="w-40" />
          </div>
          {(filterPartner || dateFrom || dateTo) && (
            <Button variant="ghost" size="sm" onClick={() => { setFilterPartner(""); setDateFrom(""); setDateTo(""); }} data-testid="clear-log-filters-btn">Clear</Button>
          )}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="logs-table">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-400">
              <tr>
                <th className="px-4 py-3 font-medium">Time</th>
                <th className="px-4 py-3 font-medium">Partner</th>
                <th className="px-4 py-3 font-medium">Key</th>
                <th className="px-4 py-3 font-medium">User</th>
                <th className="px-4 py-3 font-medium">Method</th>
                <th className="px-4 py-3 font-medium">Endpoint</th>
                <th className="px-4 py-3 font-medium">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {logs.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-400">No requests logged yet.</td></tr>
              )}
              {logs.map((l) => (
                <tr key={l.id} data-testid={`log-row-${l.id}`} className="hover:bg-slate-50/60">
                  <td className="px-4 py-2.5 text-xs text-slate-500">{fmt(l.at)}</td>
                  <td className="px-4 py-2.5 text-slate-800">{l.partner_name}</td>
                  <td className="px-4 py-2.5 font-mono text-xs text-slate-400">…{l.key_last4 || "—"}</td>
                  <td className="px-4 py-2.5 text-xs text-slate-500">{l.user_identity || "—"}</td>
                  <td className="px-4 py-2.5"><span className="rounded bg-slate-100 px-1.5 py-0.5 text-xs font-mono text-slate-600">{l.method}</span></td>
                  <td className="px-4 py-2.5 font-mono text-xs text-slate-500">{l.endpoint}</td>
                  <td className="px-4 py-2.5">
                    <span className={`rounded px-1.5 py-0.5 text-xs font-mono ${
                      l.status_code < 300 ? "bg-emerald-50 text-emerald-700"
                      : l.status_code < 500 ? "bg-amber-50 text-amber-700" : "bg-rose-50 text-rose-700"}`}>{l.status_code}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <CreatePartnerDialog open={showCreate} onClose={() => setShowCreate(false)}
        onCreated={(data) => { setShowCreate(false); setKeyData(data); loadPartners(); }} />
      <KeyRevealDialog open={!!keyData} data={keyData} onClose={() => { setKeyData(null); loadLogs(); }} />
    </div>
  );
}
